import type { JSX } from 'react';

import { Link } from 'Components/Buttons/Buttons';
import { CollapsibleModal } from 'Components/Modal/CollapsibleModal/CollapsibleModal';
import { Text } from 'Components/Text/Text';
import { iso8601ToUsDateShort } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { getExceptionDetailsUrl } from 'Helpers/URLBuilder/URLBuilder';
import { DashboardType, IssuesExceptionsDashboardType } from 'Models/Dashboards';
import { ExceptionResponse } from 'Models/Exceptions';
import { OrganizationResponseBase } from 'Models/Organization';

import styles from './TrackingExceptionsDisplayModal.module.css';

export interface TrackingExceptionsDisplayModalProps {
    hideModal: () => void;
    selectedException: ExceptionResponse[];
    date: string;
    dashboardType: IssuesExceptionsDashboardType;
    organizations?: OrganizationResponseBase[];
}

/**
 * Displays a list of exceptions that are expiring in a specific month.
 */
export const TrackingExceptionsDisplayModal = (props: TrackingExceptionsDisplayModalProps): JSX.Element => {
    const organizationIdToNameMap: Map<string, string> = new Map(props.organizations?.map((organization) => [organization.id, organization.name]));

    const sortExceptionsByDateType = (exceptionA: ExceptionResponse, exceptionB: ExceptionResponse): number => {
        if (exceptionA.expiration_date! > exceptionB.expiration_date!) {
            return 1;
        } else if (exceptionA.expiration_date! < exceptionB.expiration_date!) {
            return -1;
        } else {
            return exceptionA.title.localeCompare(exceptionB.title, undefined, { numeric: true, sensitivity: 'base' });
        }
    };

    const openExceptionListing = props.selectedException.sort(sortExceptionsByDateType).map((exception) => (
        <div className={styles.detailsContainer} key={exception.created_timestamp}>
            {props.dashboardType !== DashboardType.CONTROL_AUTOMATION && organizationIdToNameMap.size > 0 && (
                <div className={styles.detailsOrganizationName}>
                    <Text color="darkGray" noStyles={true}>
                        {organizationIdToNameMap.get(exception.impacted_vendor!)}
                    </Text>
                </div>
            )}
            <Link to={getExceptionDetailsUrl(exception.id, exception.type)} size="sm" openInNewTab>
                {exception.title}
            </Link>
            <div className={styles.detailsDateContainer}>
                <Text color="darkGray" noStyles={true}>
                    {`Expires ${iso8601ToUsDateShort(exception.expiration_date!)}`}
                </Text>
            </div>
        </div>
    ));

    return <CollapsibleModal displayItems={openExceptionListing} headerText={{ text: 'Open Exceptions', secondaryText: `${props.date}` }} hideModal={props.hideModal} />;
};
